import { NOTIFICATION_CATEGORIES, type NotificationCategory } from "./notification-meta.ts";

/** Browser (desktop/OS) notifications for the staff dashboard: preferences and the pure rules, kept apart from the React parts. */

export interface NotifyPrefs {
  enabled: boolean;
  categories: Record<NotificationCategory, boolean>;
}

export const PREFS_KEY = "villa_browser_notifications_v1";
export const DISMISSED_KEY = "villa_browser_notifications_prompt_dismissed";
export const LAST_SEEN_KEY = "villa_browser_notifications_last_seen";
export const PREFS_EVENT = "villa:notify-prefs";

export function defaultPrefs(): NotifyPrefs {
  return { enabled: false, categories: { reservations: true, payments: true, requests: true, chat: true, other: false } };
}

type ReadableStorage = Pick<Storage, "getItem">;
type WritableStorage = Pick<Storage, "setItem">;

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Stored preferences merged over the defaults; unreadable or missing storage gives the defaults. */
export function loadPrefs(storage: ReadableStorage | null = safeStorage()): NotifyPrefs {
  const base = defaultPrefs();
  try {
    const raw = storage?.getItem(PREFS_KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<NotifyPrefs>;
    const categories = { ...base.categories };
    for (const c of NOTIFICATION_CATEGORIES) if (typeof parsed.categories?.[c] === "boolean") categories[c] = parsed.categories[c] as boolean;
    return { enabled: parsed.enabled === true, categories };
  } catch {
    return base;
  }
}

export function savePrefs(prefs: NotifyPrefs, storage: WritableStorage | null = safeStorage()): void {
  try {
    storage?.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // storage full or blocked: the preference lasts for this page only
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PREFS_EVENT));
}

export type PermissionState = NotificationPermission | "unsupported";

export function permissionState(): PermissionState {
  return typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported";
}

/**
 * Whether to show an OS notification now: the user turned them on, the browser allows them, the category is wanted, and the
 * dashboard isn't the window the person is looking at (they already see it on screen).
 */
export function shouldNotify(prefs: NotifyPrefs, category: NotificationCategory, state: { permission: PermissionState; hidden: boolean; focused: boolean }): boolean {
  if (!prefs.enabled || state.permission !== "granted") return false;
  if (!prefs.categories[category]) return false;
  return state.hidden || !state.focused;
}

export interface ShowOptions {
  title: string;
  body: string;
  /** Notifications with the same tag replace each other (also across open tabs), so nothing is shown twice. */
  tag: string;
  /** Where clicking it goes, inside the dashboard. */
  url: string;
}

export async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    return (await navigator.serviceWorker.getRegistration("/")) ?? (await navigator.serviceWorker.register("/sw.js", { scope: "/" }));
  } catch {
    return null;
  }
}

/** Shows an OS notification through the service worker when there is one (needed on Android), otherwise directly. */
export async function showNotification(opts: ShowOptions): Promise<boolean> {
  if (permissionState() !== "granted") return false;
  const options: NotificationOptions = { body: opts.body, tag: opts.tag, data: { url: opts.url }, icon: "/favicon.ico" };
  try {
    const reg = await ensureServiceWorker();
    if (reg) {
      await reg.showNotification(opts.title, options);
      return true;
    }
    const n = new Notification(opts.title, options);
    n.onclick = () => {
      window.focus();
      window.dispatchEvent(new CustomEvent("villa:navigate", { detail: opts.url }));
      n.close();
    };
    return true;
  } catch {
    return false;
  }
}

/** Notifications created after `sinceMs` among the unread ones (a stored created_at is UTC "YYYY-MM-DD HH:MM:SS"). */
export function countNewerThan(items: { created_at: string }[], sinceMs: number): number {
  return items.filter((n) => Date.parse(n.created_at.includes("T") ? n.created_at : `${n.created_at.replace(" ", "T")}Z`) > sinceMs).length;
}

// ───────────────────────── Web Push ─────────────────────────
// With a push subscription the server delivers notifications through the browser's push service, so they arrive even when
// no dashboard tab is open (and on a phone, when the browser or home-screen app is closed).

/** The endpoint this browser registered with the server; set only while the server holds it. */
export const PUSH_ENDPOINT_KEY = "villa_push_endpoint";

export function pushSupported(): boolean {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** iPhone and iPad, including iPadOS reporting itself as a Mac. */
export function isAppleMobile(ua: string = typeof navigator !== "undefined" ? navigator.userAgent : "", touchPoints: number = typeof navigator !== "undefined" ? navigator.maxTouchPoints : 0): boolean {
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** On iPhone/iPad, notifications only exist for a web app opened from the Home Screen, never in a Safari tab. */
export function needsHomeScreenInstall(): boolean {
  return isAppleMobile() && !isStandalone() && !pushSupported();
}

export function isPushActive(storage: ReadableStorage | null = safeStorage()): boolean {
  try {
    return Boolean(storage?.getItem(PUSH_ENDPOINT_KEY));
  } catch {
    return false;
  }
}

function setPushEndpoint(endpoint: string | null): void {
  const storage = safeStorage();
  try {
    if (endpoint) storage?.setItem(PUSH_ENDPOINT_KEY, endpoint);
    else storage?.removeItem(PUSH_ENDPOINT_KEY);
  } catch {
    // blocked storage
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PREFS_EVENT));
}

export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const b64 = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a || a.byteLength !== b.length) return false;
  const view = new Uint8Array(a);
  return view.every((v, i) => v === b[i]);
}

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  credentials: "same-origin",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

/**
 * Makes sure this browser has a push subscription and the server has it with the current categories and language. Safe to call
 * on every dashboard load (it also refreshes a subscription after the server's key changed). Returns whether push is active.
 */
export async function syncPushSubscription(prefs: NotifyPrefs, locale: string): Promise<boolean> {
  if (!pushSupported() || permissionState() !== "granted") return false;
  try {
    if (!(await ensureServiceWorker())) return false;
    const reg = await navigator.serviceWorker.ready;
    const keyRes = await fetch("/api/push/subscription", { credentials: "same-origin" });
    const keyJson = await keyRes.json();
    if (!keyJson.success) return false;
    const serverKey = base64UrlToBytes(keyJson.data.public_key as string);

    let sub = await reg.pushManager.getSubscription();
    if (sub && !sameKey(sub.options.applicationServerKey, serverKey)) {
      await sub.unsubscribe();
      sub = null;
    }
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: serverKey });

    const res = await fetch("/api/push/subscription", jsonInit("PUT", { subscription: sub.toJSON(), categories: prefs.categories, locale }));
    const ok = Boolean((await res.json()).success);
    setPushEndpoint(ok ? sub.endpoint : null);
    return ok;
  } catch {
    setPushEndpoint(null);
    return false;
  }
}

/** Stops pushes to this browser: the server forgets it and the browser drops the subscription. */
export async function disablePush(): Promise<void> {
  setPushEndpoint(null);
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    await fetch("/api/push/subscription", jsonInit("DELETE", { endpoint: sub.endpoint })).catch(() => undefined);
    await sub.unsubscribe();
  } catch {
    // nothing to undo
  }
}

/** Asks the server to push a test message to this browser; false when push isn't set up here. */
export async function sendTestPush(): Promise<boolean> {
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return false;
    const res = await fetch("/api/push/test", jsonInit("POST", { endpoint: sub.endpoint }));
    const json = await res.json();
    return Boolean(json.success && json.data?.sent);
  } catch {
    return false;
  }
}
